import { z } from 'zod'

import { KeyboardKey, KeyboardShortcut, normalizeKeyboardShortcut, VALID_KEYS } from '~/astra'
import { Schemas } from '~/clients/api-client'
import { PractitionerStatusDefinition } from '~/utils/surgeryActivities'

export type TimeOfDay = Schemas['TimeOfDay']

export const statusDefinitionColorsByAssignment = {
    surgery: ['#D5EFFF', '#D0E3FF', '#C1D0FF'],
    policlinic: ['#FFEAB0', '#FFDFB5', '#FEDCE7'],
    on_call_surgery: ['#E1D9FF', '#EDD5FF', '#D4C4FF'],
    unavailable: ['#E1DCCF', '#E2F0BD', '#D6F1E3', '#B3ECDE', '#F0F0F3'],
} as const

export const timeOfDayOptions: Array<{ label: string; value: TimeOfDay }> = [
    { label: 'Dagtid', value: 'day_time' },
    { label: 'Kveld', value: 'evening' },
] as const

export const getStatusDefinitionColorsByAssignment = (assignment: string) => {
    if (assignment === 'surgery') return statusDefinitionColorsByAssignment.surgery
    if (assignment === 'policlinic') return statusDefinitionColorsByAssignment.policlinic
    if (assignment === 'on_call_surgery') return statusDefinitionColorsByAssignment.on_call_surgery
    return statusDefinitionColorsByAssignment.unavailable
}

export const isAllowedStatusDefinitionColor = (assignment: string, color: string) => getStatusDefinitionColorsByAssignment(assignment).includes(color)

export type ExistingShortcut = {
    keyboard_shortcut: string
    keyboard_has_shift: boolean
    keyboard_has_meta_key: boolean
}

export const isShortcutTaken = (existingShortcuts: ExistingShortcut[], key: string, hasShift: boolean, hasMeta: boolean): boolean =>
    existingShortcuts.some(
        existing =>
            normalizeKeyboardShortcut(existing.keyboard_shortcut) === normalizeKeyboardShortcut(key) &&
            existing.keyboard_has_shift === hasShift &&
            existing.keyboard_has_meta_key === hasMeta
    )

/**
 * Defines the priority queue for keyboard shortcut suggestions.
 * Order: letter → Shift + letter → Meta + Shift + letter
 */
const buildShortcutCandidates = (key: KeyboardKey): KeyboardShortcut[] => [
    { keyboardKey: key, hasShift: false, hasMeta: false },
    { keyboardKey: key, hasShift: true, hasMeta: false },
    { keyboardKey: key, hasShift: true, hasMeta: true },
]

const shortcutsMatch = (a: KeyboardShortcut, b: KeyboardShortcut): boolean =>
    a.keyboardKey.toUpperCase() === b.keyboardKey.toUpperCase() &&
    (a.hasShift ?? false) === (b.hasShift ?? false) &&
    (a.hasMeta ?? false) === (b.hasMeta ?? false)

/**
 * Suggests a keyboard shortcut based on the first letter of the title.
 * Priority: letter → Shift + letter → Meta + Shift + letter → null
 *
 * When `currentShortcut` is provided, starts searching from the next
 * candidate after the current one in the queue. This allows cycling
 * through suggestions on repeated clicks.
 */
export const suggestShortcut = (title: string, existingShortcuts: ExistingShortcut[], currentShortcut?: KeyboardShortcut): KeyboardShortcut | null => {
    const firstLetter = title.trim().charAt(0).toUpperCase()
    if (!firstLetter || !VALID_KEYS.has(firstLetter as KeyboardKey)) return null

    const key = firstLetter as KeyboardKey
    const candidates = buildShortcutCandidates(key)

    // Determine where to start in the queue
    let startIndex = 0
    if (currentShortcut && currentShortcut.keyboardKey.toUpperCase() === key) {
        const currentIndex = candidates.findIndex(c => shortcutsMatch(c, currentShortcut))
        if (currentIndex !== -1) {
            startIndex = currentIndex + 1
        }
    }

    // Find next available candidate from startIndex
    for (let i = startIndex; i < candidates.length; i++) {
        const candidate = candidates[i]
        if (candidate && !isShortcutTaken(existingShortcuts, candidate.keyboardKey, candidate.hasShift ?? false, candidate.hasMeta ?? false)) {
            return candidate
        }
    }

    return null
}

export const createStatusDefinitionFormSchema = (assignmentName: string, existingShortcuts: ExistingShortcut[]) => {
    const allowedColors = getStatusDefinitionColorsByAssignment(assignmentName)

    return z
        .object({
            title: z.string().min(1, 'Tittel er påkrevd'),
            label: z.string().min(1, 'Forkortelse er påkrevd').max(4, 'Maks 4 tegn'),
            timeOfDay: z.enum(['day_time', 'evening']).optional(),
            departments: z.array(z.number()),
            color: z
                .string()
                .min(1, 'Velg en farge')
                .refine(color => allowedColors.includes(color), 'Velg en farge fra listen'),
            keyboardShortcut: z.string().min(1, 'Hurtigtast er påkrevd').max(1, 'Kun ett tegn'),
            keyboardHasShift: z.boolean(),
            keyboardHasMeta: z.boolean(),
        })
        .superRefine((data, ctx) => {
            if (isShortcutTaken(existingShortcuts, data.keyboardShortcut, data.keyboardHasShift, data.keyboardHasMeta)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Denne hurtigtasten er allerede i bruk',
                    path: ['keyboardShortcut'],
                })
            }
        })
}

export type StatusDefinitionFormValues = z.infer<ReturnType<typeof createStatusDefinitionFormSchema>>

export const toKeyboardShortcut = (key: string, hasShift: boolean, hasMeta: boolean): KeyboardShortcut => ({
    keyboardKey: key as KeyboardKey,
    hasShift,
    hasMeta,
})

// The format is all capitalized letters and the special characters are all replaced with _
// I also added Norwegian letters resolver because they can be used in the title but not in the code,
// and we want to preserve as much of the original title as possible in the code for readability
// because they will be replaced by [^A-Z0-9] regex later
export const convertTitleToCode = (title: string): string =>
    title
        .toUpperCase()
        .replace(/Æ/g, 'AE')
        .replace(/Ø/g, 'OE')
        .replace(/Å/g, 'AA')
        .replace(/[^A-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')

export const getDefaultValues = (
    assignmentName: string,
    resolvedDepartmentIds: number[],
    statusDefinition?: PractitionerStatusDefinition
): StatusDefinitionFormValues => {
    const assignmentColors = getStatusDefinitionColorsByAssignment(assignmentName)
    const fallbackColor = assignmentColors[0]
    const selectedColor =
        statusDefinition?.color && isAllowedStatusDefinitionColor(assignmentName, statusDefinition.color) ? statusDefinition.color : fallbackColor

    return {
        title: statusDefinition?.title ?? '',
        label: statusDefinition?.label ?? '',
        timeOfDay: statusDefinition?.time_of_day ?? undefined,
        departments: resolvedDepartmentIds,
        color: selectedColor,
        keyboardShortcut: statusDefinition?.keyboard_shortcut ? normalizeKeyboardShortcut(statusDefinition.keyboard_shortcut) : '',
        keyboardHasShift: statusDefinition?.keyboard_has_shift ?? false,
        keyboardHasMeta: statusDefinition?.keyboard_has_meta_key ?? false,
    }
}
